import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  BookOpen, 
  Calendar,
  MapPin,
  Users,
  Scroll,
  Heart,
  Sparkles
} from 'lucide-react';

export const SaintHistoryCard = ({ 
  saintName,
  introduction,
  epoch,
  location,
  origin,
  lifeStory,
  symbolism,
  feastDay,
  history // fallback for old format
}) => {
  const { language } = useLanguage();
  
  // If we have the new detailed format
  const hasDetailedInfo = introduction || epoch || lifeStory;

  if (!hasDetailedInfo && !history) {
    return null;
  }

  return (
    <Card className="card-divine mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-gold" />
          {language === 'fr' ? 'Histoire du Saint' : 'Saint\'s History'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasDetailedInfo ? (
          <>
            {/* Introduction */}
            {introduction && (
              <div className="pb-4 border-b border-border/50">
                <p className="text-lg text-foreground/90 leading-relaxed font-heading italic">
                  {introduction}
                </p>
              </div>
            )}

            {/* Qui était-il/elle ? */}
            {(epoch || location || origin) && (
              <div className="space-y-3">
                <h4 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
                  <Users className="w-4 h-4 text-gold" />
                  {language === 'fr' ? 'Qui était-il/elle ?' : 'Who were they?'}
                </h4>
                <div className="grid sm:grid-cols-3 gap-4 pl-6">
                  {epoch && (
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          {language === 'fr' ? 'Époque' : 'Era'}
                        </span>
                        <span className="text-foreground">{epoch}</span>
                      </div>
                    </div>
                  )}
                  {location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          {language === 'fr' ? 'Lieu' : 'Location'}
                        </span>
                        <span className="text-foreground">{location}</span>
                      </div>
                    </div>
                  )}
                  {origin && (
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          {language === 'fr' ? 'Origine' : 'Origin'}
                        </span>
                        <span className="text-foreground">{origin}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sa vie / Son martyre */}
            {lifeStory && (
              <div className="space-y-3">
                <h4 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
                  <Scroll className="w-4 h-4 text-gold" />
                  {language === 'fr' ? 'Sa vie' : 'Their Life'}
                </h4>
                <p className="text-foreground/90 leading-relaxed pl-6">
                  {lifeStory}
                </p>
              </div>
            )}

            {/* Ce qu'il/elle symbolise */}
            {symbolism && symbolism.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-gold" />
                  {language === 'fr' ? 'Ce qu\'il/elle symbolise' : 'What they symbolize'}
                </h4>
                <ul className="pl-6 space-y-2">
                  {symbolism.map((symbol, index) => (
                    <li key={index} className="flex items-center gap-2 text-foreground/90">
                      <Heart className="w-3 h-3 text-accent shrink-0" />
                      {symbol}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fête liturgique */}
            {feastDay && (
              <div className="pt-4 border-t border-border/50">
                <div className="flex items-center gap-2 text-foreground">
                  <Calendar className="w-4 h-4 text-gold" />
                  <span className="font-medium">
                    {language === 'fr' ? 'Fête liturgique :' : 'Liturgical feast:'}
                  </span>
                  <span className="font-heading text-lg text-gold">{feastDay}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          // Fallback to simple history display
          <p className="text-foreground/90 leading-relaxed">
            {history}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SaintHistoryCard;
